import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Heart, ChevronRight, Loader2 } from 'lucide-react';
import { Heading, BodyText, SmallText } from '../components/brand/BrandTypography';
import { PrimaryButton, TertiaryButton } from '../components/brand/BrandButton';
import { AdminEditButton } from '../components/admin/AdminEditButton';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Impact = () => {
    const navigate = useNavigate();
    const [pageContent, setPageContent] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        const fetchPageContent = async () => {
            try {
                const response = await fetch(`${API_URL}/api/pages/impact`);
                if (!response.ok) throw new Error('Failed to fetch page content');
                const data = await response.json();
                setPageContent(data);
            } catch (err) {
                console.error('Error fetching impact page:', err);
                setError(err.message);
            } finally {
                setLoading(false);
            }
        };
        fetchPageContent();
    }, []);

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center" style={{ background: 'linear-gradient(180deg, #FEF6EF 0%, #FEF6EF 100%)' }}>
                <Loader2 className="w-8 h-8 animate-spin text-[#1D6B6B]" />
            </div>
        );
    }

    if (error || !pageContent) {
        return (
            <div className="min-h-screen flex items-center justify-center" style={{ background: 'linear-gradient(180deg, #FEF6EF 0%, #FEF6EF 100%)' }}>
                <div className="text-center">
                    <BodyText className="text-[#7D8E95] mb-4">Unable to load page content</BodyText>
                    <TertiaryButton onClick={() => navigate(-1)}>Go Back</TertiaryButton>
                </div>
            </div>
        );
    }

    // Sort sections by order
    const sortedSections = [...(pageContent.sections || [])].sort((a, b) => a.order - b.order);

    return (
        <div className="min-h-screen" style={{ background: 'linear-gradient(180deg, #FEF6EF 0%, #FEF6EF 100%)' }}>
            {/* Admin Edit Button */}
            <AdminEditButton tab="pages" />
            
            {/* Header */}
            <div className="sticky top-0 z-10 glass-header">
                <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
                    <button 
                        onClick={() => navigate(-1)}
                        className="flex items-center gap-2 text-[#2A8585] hover:text-[#1D6B6B] transition-colors"
                        data-testid="back-btn"
                    >
                        <ArrowLeft className="w-5 h-5" />
                        <span className="text-sm font-medium">Back</span>
                    </button>
                    <Heading as="span" size="text-base" weight="semibold" className="text-[#1D6B6B]">
                        Our Story
                    </Heading>
                    <div className="w-16" />
                </div>
            </div>

            {/* Content - Story Format */}
            <div className="max-w-2xl mx-auto px-6 py-12">
                {/* Hero Section */}
                <div className="text-center mb-16">
                    <div 
                        className="w-20 h-20 mx-auto mb-6 rounded-full flex items-center justify-center"
                        style={{
                            background: 'linear-gradient(135deg, rgba(52,70,72,0.2) 0%, rgba(125,142,149,0.15) 100%)'
                        }}
                    >
                        <Heart className="w-10 h-10 text-[#1D6B6B]" />
                    </div>
                    <Heading as="h1" size="text-3xl md:text-4xl" weight="medium" className="text-[#1D6B6B]">
                        Our Story, Our Mission
                    </Heading>
                </div>

                {/* Story Narrative - Flowing prose format */}
                <div className="space-y-8">
                    {/* Opening */}
                    <div className="text-center mb-12">
                        <BodyText size="lg" className="text-[#2A8585] leading-relaxed italic">
                            {pageContent.hero_text || "CoRegulate was born from lived experience — the kind that knows how hard it is to find support that actually understands ADHD nervous systems."}
                        </BodyText>
                    </div>

                    {/* Story sections as flowing narrative */}
                    {sortedSections.filter(s => s.is_visible).map((section, index) => {
                        // Render each section as part of a flowing story
                        const isLastSection = index === sortedSections.filter(s => s.is_visible).length - 1;
                        
                        return (
                            <div 
                                key={section.section_id} 
                                className={`${isLastSection ? 'mt-12' : ''}`}
                            >
                                {/* Section heading as story chapter marker */}
                                <div className="flex items-center gap-4 mb-6">
                                    <div className="flex-1 h-px bg-gradient-to-r from-transparent via-[#FFBB98] to-transparent" />
                                    <SmallText className="text-[#1D6B6B] font-medium tracking-wide">
                                        {section.title}
                                    </SmallText>
                                    <div className="flex-1 h-px bg-gradient-to-r from-transparent via-[#FFBB98] to-transparent" />
                                </div>

                                {/* Story content */}
                                <div className="text-center px-4">
                                    <BodyText className="text-[#2A8585] leading-[1.9] whitespace-pre-line">
                                        {section.content}
                                    </BodyText>
                                    
                                    {/* Quote as emphasized story element */}
                                    {section.quote && (
                                        <div 
                                            className="mt-8 py-6 px-8 rounded-2xl mx-auto max-w-lg"
                                            style={{
                                                background: 'linear-gradient(135deg, rgba(52,70,72,0.08) 0%, rgba(125,142,149,0.06) 100%)',
                                                borderLeft: '3px solid rgba(52,70,72,0.4)'
                                            }}
                                        >
                                            <BodyText className="text-[#7D8E95] italic leading-relaxed">
                                                {section.quote}
                                            </BodyText>
                                        </div>
                                    )}
                                </div>
                            </div>
                        );
                    })}

                    {/* Closing flourish */}
                    <div className="pt-8 text-center">
                        <div className="inline-flex items-center gap-3 text-[#1D6B6B]">
                            <div className="w-8 h-px bg-[#1D6B6B]/30" />
                            <Heart className="w-4 h-4" />
                            <div className="w-8 h-px bg-[#1D6B6B]/30" />
                        </div>
                    </div>
                </div>

                {/* CTA */}
                <div className="text-center mt-16">
                    <BodyText className="text-[#7D8E95] mb-6">
                        Thank you for being part of this journey.
                    </BodyText>
                    <div className="flex flex-col sm:flex-row gap-4 justify-center">
                        <TertiaryButton 
                            onClick={() => navigate('/pricing')}
                            className="px-6"
                            data-testid="view-plans-btn"
                        >
                            {pageContent.cta_secondary_text || 'View plans'}
                        </TertiaryButton>
                        <PrimaryButton 
                            onClick={() => navigate('/initial-assessment')}
                            className="px-6"
                            data-testid="start-regulating-btn"
                        >
                            {pageContent.cta_primary_text || 'Start regulating'}
                            <ChevronRight className="w-4 h-4 ml-1" />
                        </PrimaryButton>
                    </div>
                </div>

                {/* Footer Note */}
                {pageContent.contact_email && (
                    <div className="mt-16 text-center">
                        <SmallText className="text-[#7D8E95]">
                            Questions about our story? We'd love to hear from you.<br />
                            <a href={`mailto:${pageContent.contact_email}`} className="text-[#1D6B6B] hover:underline">
                                {pageContent.contact_email}
                            </a>
                        </SmallText>
                    </div>
                )}
            </div>
        </div>
    );
};

export default Impact;
